import * as SliderPrimitive from "@radix-ui/react-slider";
import { ZoomIn, ZoomOut } from "lucide-react";
import {
  SURFACE_CONTROL_CLS,
  SURFACE_ICON_BTN_CLS,
} from "@/react/components/entityViews/files/constants/surfaceChrome";
import { cn } from "@/modern/utils";
import {
  IMAGE_ZOOM_BUTTON_STEP,
  MAX_IMAGE_ZOOM,
  MIN_IMAGE_ZOOM,
} from "../hooks/useImagePanZoom";

type ImageZoomControlsProps = {
  scale: number;
  step: number;
  onScaleChange: (scale: number) => void;
  onReset: () => void;
  disabled?: boolean;
  visible?: boolean;
};

export const ImageZoomControls = ({
  scale,
  step,
  onScaleChange,
  onReset,
  disabled = false,
  visible = true,
}: ImageZoomControlsProps) => {
  if (disabled) return null;

  const atMin = scale <= MIN_IMAGE_ZOOM;
  const atMax = scale >= MAX_IMAGE_ZOOM;

  return (
    <div
      data-zoom-control
      className={cn(
        "pointer-events-auto absolute bottom-4 left-1/2 z-[35] -translate-x-1/2",
        "transition-opacity duration-150",
        visible ? "opacity-100" : "opacity-0 group-hover:opacity-100",
      )}
      onPointerDown={(event) => event.stopPropagation()}
    >
      <div className="flex items-center gap-1.5" role="group" aria-label="Image zoom">
        <button
          type="button"
          className={cn(
            SURFACE_ICON_BTN_CLS,
            "inline-flex size-8 items-center justify-center shadow-md disabled:opacity-40",
          )}
          onClick={() => onScaleChange(scale - IMAGE_ZOOM_BUTTON_STEP)}
          disabled={atMin}
          aria-label="Zoom out"
        >
          <ZoomOut className="size-3.5" aria-hidden />
        </button>

        <div
          className={cn(
            SURFACE_CONTROL_CLS,
            "flex items-center gap-2.5 rounded-full px-3 py-1.5 shadow-md",
          )}
        >
          <SliderPrimitive.Root
            className="relative flex h-5 w-28 touch-none select-none items-center sm:w-32"
            min={MIN_IMAGE_ZOOM}
            max={MAX_IMAGE_ZOOM}
            step={step}
            value={[scale]}
            onValueChange={([value]) => onScaleChange(value)}
            aria-label="Image zoom level"
          >
            <SliderPrimitive.Track className="relative h-1 w-full grow overflow-hidden rounded-full bg-background">
              <SliderPrimitive.Range className="absolute h-full rounded-full bg-foreground/35" />
            </SliderPrimitive.Track>
            <SliderPrimitive.Thumb
              className={cn(
                "block size-3.5 rounded-full bg-foreground shadow-sm",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-muted",
              )}
            />
          </SliderPrimitive.Root>

          <div className="h-4 w-px shrink-0 bg-border" aria-hidden />

          <button
            type="button"
            className={cn(
              "min-w-[2.75rem] shrink-0 text-xs font-medium tabular-nums leading-none text-foreground",
              "rounded-full px-1 py-0.5 transition-colors hover:bg-background/80",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-muted",
            )}
            onClick={onReset}
            aria-label="Reset zoom to fit"
            title="Reset to fit"
          >
            {Math.round(scale * 100)}%
          </button>
        </div>

        <button
          type="button"
          className={cn(
            SURFACE_ICON_BTN_CLS,
            "inline-flex size-8 items-center justify-center shadow-md disabled:opacity-40",
          )}
          onClick={() => onScaleChange(scale + IMAGE_ZOOM_BUTTON_STEP)}
          disabled={atMax}
          aria-label="Zoom in"
        >
          <ZoomIn className="size-3.5" aria-hidden />
        </button>
      </div>
    </div>
  );
};
